import { MEMBER_AVATAR_ALLOWED_CONTENT_TYPES, MEMBER_AVATAR_MAX_SIZE_BYTES } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';

// The chosen file is not a permitted profile photo (wrong type or too large).
// Caught before any request; the media edge policy + the confirm leaf remain the
// authoritative server-side gate.
export class PhotoPolicyError extends Error {}

// The presign / direct-to-S3 upload / confirm round-trip failed.
export class PhotoUploadError extends Error {}

export function isAllowedPhoto(file: File): boolean {
  return (
    (MEMBER_AVATAR_ALLOWED_CONTENT_TYPES as ReadonlyArray<string>).includes(file.type) &&
    file.size <= MEMBER_AVATAR_MAX_SIZE_BYTES
  );
}

/**
 * How long the whole presign → store → confirm round-trip may take before it is
 * abandoned, so a stalled transfer cannot latch the dialog's busy flag. Sized
 * against the 5 MB ceiling (`MEMBER_AVATAR_MAX_SIZE_BYTES`): ≈0.7 Mbit/s of
 * usable upstream clears it. Do not copy it to a helper with a different ceiling.
 */
export const PHOTO_UPLOAD_DEADLINE_MS = 60_000;

async function runUpload(memberId: string, file: File, signal: AbortSignal): Promise<string> {
  const created = await apiClient.adminMembers.createAvatarUpload({
    pathParams: { id: memberId },
    body: { contentType: file.type, sizeBytes: file.size, originalFilename: file.name },
  });

  if (!created.ok) {
    throw new PhotoUploadError();
  }

  const { fileId, upload } = created.body;

  const form = new FormData();

  for (const [name, value] of Object.entries(upload.fields)) {
    form.append(name, value);
  }

  form.append('file', file);

  const stored = await fetch(upload.url, { method: 'POST', body: form, signal });

  if (!stored.ok) {
    throw new PhotoUploadError();
  }

  const confirmed = await apiClient.adminMembers.confirmAvatarUpload({
    pathParams: { id: memberId, fileId },
    body: {},
  });

  if (!confirmed.ok) {
    throw new PhotoUploadError();
  }

  return confirmed.body.id;
}

/**
 * Runs the operator two-phase upload of a member's profile photo: presign, POST
 * the bytes straight to object storage, confirm. Returns the confirmed file id,
 * which "Speichern" then attaches as `avatarMediaId`. Throws `PhotoPolicyError`
 * for a file outside the avatar policy (nothing is sent) and `PhotoUploadError`
 * for every other failure, including a round-trip past `PHOTO_UPLOAD_DEADLINE_MS`.
 */
export async function uploadMemberPhoto(memberId: string, file: File): Promise<string> {
  if (!isAllowedPhoto(file)) {
    throw new PhotoPolicyError();
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PHOTO_UPLOAD_DEADLINE_MS);
  // The deadline as a promise, so it also covers the presign and confirm legs —
  // they go through the generated client, which takes no per-call signal.
  const deadline = new Promise<never>((_, reject) => {
    controller.signal.addEventListener('abort', () => reject(new PhotoUploadError()));
  });

  try {
    return await Promise.race([runUpload(memberId, file, controller.signal), deadline]);
  } catch (error) {
    throw error instanceof PhotoUploadError ? error : new PhotoUploadError();
  } finally {
    clearTimeout(timer);
  }
}
